/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { PreFooterData, NewResponse } from '../interface';
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { getPathnameFromAsPath, parseUrl } from '@/utils/urlHelpers';
import { type AxiosInstance } from 'axios';
import { createQuery } from '@/utils/createReactQuery';
import { usePageType } from '@/features/category/api/getPageType';
import { useCMSCategoryData } from '@/features/category/api/getCMSCategoryData';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { mergePrefooterBackendWithCMSData } from '../utils/mergePrefooterBackendWithCMSData';
import { updatePrefooterBackendLinks } from '../utils/updatePrefooterBackendLinks';
import { getPageDependentPrefooterParams } from '../utils/getPageDependentPrefooterParams';
import { useContentStackDefaultPDPContent } from '@/features/pdp/api/getContentStackDefaultProductContent';
import { useContentStackPremiumPDPContent } from '@/features/pdp/api/getContentStackPremiumProductContent';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { pageTypes } from '@/constants/page';
import { countryCodes } from '@/constants/locale';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v2/seo-footer-mappings`;

const ACES_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v3/seo-footer-mappings`;

const ES_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v4/seo-footer-mappings`;

type PreFooterQueryOptions = {
  locale: Locale;
  seourl: string;
  filterName?: string;
  pageType?: string;
  catalogRepositoryId?: string;
  categoryIdPath: string | undefined;
  taxonomyPath?: string;
  shopByMakeName: string | undefined;
  shopByMakeId: string | undefined;
  shopByModelName: string | undefined;
  shopByYear: string | undefined;
  shopByMakeActive?: boolean;
  shopByModelActive?: boolean;
  shopByYearActive?: boolean;
  vehicleOptions: {
    vehicleId: string | undefined;
    vehicleTypeId: number | undefined;
    makeId: number | undefined;
    modelId: number | undefined;
    year: string | undefined;
    vehicleQuestions: Record<string, string | number> | undefined;
  };
  shouldCallACES: boolean;
  experimentId: string | undefined;
};

const { useData: usePreFooterData, prefetch: prefetchPreFooterData } = createQuery<
  PreFooterData,
  PreFooterQueryOptions
>('preFooterData', async (options, axiosInstance) => {
  if (options.locale === countryCodes.ptBr) {
    return [];
  }

  if (options.shouldCallACES) {
    return getAcesPreFooterData(options, axiosInstance);
  }

  return getPreFooterDataForCMS(options, axiosInstance);
});

const getAcesPreFooterData = async (
  {
    locale,
    pageType,
    filterName,
    categoryIdPath,
    catalogRepositoryId,
    shopByMakeName,
    shopByModelName,
    shopByYear,
    shopByMakeId,
    shopByMakeActive,
    shopByModelActive,
    experimentId,
    vehicleOptions: { vehicleTypeId, makeId, modelId, year, vehicleQuestions },
  }: PreFooterQueryOptions,
  axiosInstance?: AxiosInstance,
) => {
  const params = {
    country: getCountryFromLocale(locale),
    customerType: 'B2C',
    pageType: pageType === 'ProductShelf' || pageType === 'ProductDetails' ? 'PRODUCT' : 'CATEGORY',
    repositoryId: catalogRepositoryId,
    categoryIdPath,
    filterName,
    shopByMakeName,
    shopByModelName,
    shopByYear,
    shopByMakeId,
    shopByMakeActive,
    shopByModelActive,
    vehicleTypeId,
    makeId,
    modelId,
    year,
    vehicleQuestions: vehicleQuestions
      ? extractVehicleQuestions(vehicleQuestions, true)
      : undefined,
  };

  const isES = experimentId === 'vertex' || experimentId === 'elasticsearch';
  const response = await getAxios(axiosInstance).get<NewResponse>(isES ? ES_URL : ACES_URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });
  return selector(response.data);
};

const getPreFooterDataForCMS = async (
  {
    seourl,
    filterName,
    pageType,
    catalogRepositoryId,
    taxonomyPath,
    shopByMakeName,
    shopByModelName,
    shopByYear,
    shopByMakeActive,
    shopByModelActive,
    shopByYearActive,
    locale,
    vehicleOptions: { vehicleId },
  }: PreFooterQueryOptions,
  axiosInstance?: AxiosInstance,
) => {
  const params = {
    country: getCountryFromLocale(locale),
    customerType: 'B2C',
    filterName: filterName,
    pageType: pageType === 'ProductShelf' || pageType === 'ProductDetails' ? 'PRODUCT' : 'CATEGORY',
    preview: showXMPreviewDate(),
    seoRepositoryId: catalogRepositoryId,
    seoUrl: taxonomyPath ?? seourl,
    shopByMakeActive,
    shopByModelActive,
    shopByYearActive,
    vehicleMake: shopByMakeName,
    vehicleModel: shopByModelName,
    vehicleYear: shopByYear,
    ...(vehicleId && { vehicleId }),
  };
  const response = await getAxios(axiosInstance).get<NewResponse>(URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return selector(response.data);
};

// TODO: remove legacy PreFooter types and mapping
const selector = (data: NewResponse): PreFooterData => {
  return [
    {
      preFooterMiddle: data.map((item) => {
        return {
          name: item.contentGroupTitle ?? '',
          contents: [
            {
              numItemsDisplay: '10',
              title: item.contentGroupTitle ?? '',
              tablet: true,
              mobile: true,
              desktop: true,
              name: item.contentGroupTitle ?? '',
              content: (item.contentGroupMappings ?? []).map((mapping) => ({
                tablet: true,
                mobile: true,
                desktop: true,
                CTALink: mapping.linkUrl,
                name: mapping.linkLabel,
              })),
            },
          ],
        };
      }),
      contents: [],
    },
  ];
};

const usePreFooterMiddleData = () => {
  const { asPath, pathname } = useLegacyRouter();
  const locale = useLocale();
  const seourl = getPathnameFromAsPath(asPath).replace(/(\/bn\/|\/b\/).*/, '');
  const preferredVehicle = usePreferredVehicle();
  const vehicleId = preferredVehicle?.catalogVehicleId ?? '0';
  const { data: pageTypeData, isSuccess: pageTypeSuccess } = usePageType();
  const isPDP = pathname === '/products/[...slug]';
  const isShelf = pageTypeData?.pageType?.toLowerCase().includes(pageTypes.shelf);
  const { acesEnabled } = useAcesFlags();
  const experimentId = useExperimentId();

  const {
    data: productDetails,
    isSuccess: areProductDetailsSuccess,
    isFetching: areProductDetailsFetching,
  } = useProductDetails({
    enabled: isPDP,
  });
  const { data: plpCMSData, isCSProductListingPageEnabled } = useContentStackPLPData({
    enabled: false,
  });

  const { data: catgeoryCMSData, enabled: isCatgeoryCMSEnabled } = useCMSCategoryData();

  const taxonomyPath = productDetails?.product?.cmsContentPath ?? '';
  const brandName =
    productDetails?.product?.subBrandName ?? productDetails?.product?.brandName ?? '';

  const { data: defaultPDPContent } = useContentStackDefaultPDPContent({
    enabled: isPDP && areProductDetailsSuccess && !areProductDetailsFetching,
    taxonomyPath,
  });
  const isEnhancedPdpEnabled = useForteDecisionFlagWithTrueDefault('enhancedPdpEnabled');
  const premiumBrands: string[] = defaultPDPContent?.premiumBrands?.brands ?? [];

  const { data: premiumPDPContent } = useContentStackPremiumPDPContent({
    enabled:
      !isEnhancedPdpEnabled &&
      isPDP &&
      areProductDetailsSuccess &&
      !areProductDetailsFetching &&
      premiumBrands.includes(productDetails?.product?.brandName ?? ''),
    taxonomyPath,
    brandName,
  });

  const prefooterContentStack = isCatgeoryCMSEnabled
    ? catgeoryCMSData
    : isPDP
      ? (premiumPDPContent ?? defaultPDPContent)
      : plpCMSData;

  const pageDependentParams = getPageDependentPrefooterParams({
    isPDP,
    pageTypeData,
    productDetails,
    acesEnabled,
  });

  const isUS = locale === 'en-US';
  const isCMSDisabled =
    !(isCSProductListingPageEnabled && isShelf) && !isPDP && !isCatgeoryCMSEnabled;

  const partsProQueryEnabled = !isCMSDisabled
    ? isPDP
      ? !!productDetails?.product?.categoryId
      : true
    : isUS;

  const acesQueryEnabled = Boolean(
    !isCMSDisabled
      ? isPDP
        ? productDetails?.product?.categoryId && productDetails?.product?.partGroupId
        : pageTypeSuccess && pageTypeData?.catalogRepositoryId && pageTypeData?.catalogId
      : isUS,
  );

  return usePreFooterData({
    locale,
    seourl,
    enabled: acesEnabled ? acesQueryEnabled : partsProQueryEnabled,
    ...pageDependentParams,
    shouldCallACES: acesEnabled,
    experimentId,
    vehicleOptions: acesEnabled
      ? {
          vehicleId: undefined,
          vehicleTypeId: preferredVehicle?.vehicleTypeId,
          makeId: preferredVehicle?.makeId,
          modelId: preferredVehicle?.modelId,
          year: preferredVehicle?.year,
          vehicleQuestions: preferredVehicle?.vehicleQuestionsIds,
        }
      : {
          vehicleId,
          vehicleTypeId: undefined,
          makeId: undefined,
          modelId: undefined,
          year: undefined,
          vehicleQuestions: undefined,
        },

    select: (data) => {
      if (isCMSDisabled) {
        return data[0]?.preFooterMiddle ?? [];
      }

      const prefooterColumns = updatePrefooterBackendLinks(
        data[0].preFooterMiddle,
        (isPDP ? productDetails?.product?.seoUrl : pageTypeData?.canonicalPath) ??
          parseUrl(asPath).url,
        acesEnabled,
      );

      return mergePrefooterBackendWithCMSData(
        prefooterColumns,
        prefooterContentStack?.prefooter?.prefooter?.[0].column,
        isCatgeoryCMSEnabled,
      );
    },
  });
};

export { usePreFooterData, usePreFooterMiddleData, prefetchPreFooterData };
